import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../../../styles/Category.scss';
import { tryFetch } from 'Helpers/FetchAPI';
import SkeletonComponent from 'Components/Skeleton';

interface Merchant {
  id: string;
  merchant_name: string;
  category: string;
  hero_url: string;
  logo_url?: string;
  merchant_slug: string;
  status: string;
}

const Category: React.FC = () => {
  const { categoryName = '' } = useParams();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortAZ, setSortAZ] = useState<'a-z' | 'z-a'>('a-z');
  const [sortDate, setSortDate] = useState<'newest' | 'oldest'>('newest');


    const formattedTitle =
      ['others', 'more-choices-more-love'].includes(categoryName.toLowerCase())
          ? 'More Choices, More Love!'
          : categoryName.replace(/-/g, ' ');

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setLoading(true);
        const response = await tryFetch({ url: '/merchants', method: 'GET' });
        if (!response.is_success) throw new Error('Fetch failed');

        const allMerchants = response.data as Merchant[];
        const filtered = allMerchants.filter((m) =>
            m.category.toLowerCase() ===
            (categoryName === 'more-choices-more-love'
              ? 'others'
              : categoryName.replace(/-/g, ' ')
            ).toLowerCase()
          );

        setMerchants(filtered);
        setFilteredMerchants(filtered);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchMerchants();
  }, [categoryName]);

   const handleAZToggle = () => {
    const newSort = sortAZ === 'a-z' ? 'z-a' : 'a-z';
    setSortAZ(newSort);
    setFilter(newSort);

    const sorted = [...merchants].sort((a, b) =>
      newSort === 'a-z'
        ? a.merchant_name.localeCompare(b.merchant_name)
        : b.merchant_name.localeCompare(a.merchant_name)
    );

    setFilteredMerchants(sorted);
  };

   const handleDateToggle = () => {
    const newSort = sortDate === 'newest' ? 'oldest' : 'newest';
    setSortDate(newSort);
    const newFilter = newSort === 'newest' ? 'new-old' : 'old-new';
    setFilter(newFilter);

    const sorted = [...merchants].sort((a, b) =>
      newSort === 'newest'
        ? b.id.localeCompare(a.id)
        : a.id.localeCompare(b.id)
    );

    setFilteredMerchants(sorted);
  };

  const handleAll = () => {
    setFilter('all');
    setFilteredMerchants(merchants);
  };


  return (
    <div className="category-page">
      <h2 className="category-title">{formattedTitle}</h2>

      <div className="filter-tabs">
        <button onClick={handleAll} className={filter === 'all' ? 'active' : ''}>All</button>
        <button onClick={handleAZToggle} className={filter === 'a-z' || filter === 'z-a' ? 'active' : ''}>
          {sortAZ === 'a-z' ? 'A → Z ' : 'Z → A '}
        </button>
          <button onClick={handleDateToggle} className={filter === 'new-old' || filter === 'old-new' ? 'active' : ''}>
          {sortDate === 'newest' ? 'Newest → Oldest' : 'Oldest → Newest'}
        </button>
      </div>

      <div className="merchant-grid">
  {loading
    ? Array(6).fill(0).map((_, i) => <SkeletonComponent key={i} />)
    : filteredMerchants.map((merchant) => (
        <a
          key={merchant.id}
          href={`/merchant/${merchant.merchant_slug}`}
          className={`merchant-card ${merchant.status === 'INACTIVE' ? 'inactive' : ''}`}
        >
          <div
            className="merchant-hero"
            style={{ backgroundImage: `url(${merchant.hero_url})` }}
          >
            <div className="overlay"></div>
            {merchant.logo_url && (
              <img
                src={merchant.logo_url}
                alt={merchant.merchant_name}
                className="merchant-logo"
              />
            )}
          </div>
          <p className="merchant-name">{merchant.merchant_name}</p>
        </a>
      ))}
</div>
    </div>
  );
};

export default Category;
